import type { ReactNode } from 'react';

import type { CollectionGap, CollectionImagePosition } from '@/lib/blocks/collection/blocks';
import { getCardContentGapClassName } from '@/lib/blocks/collection/blocks';
import { cn } from '@/lib/utils';
import type { CollectionCardSideBySideMode } from './types';

export type SideBySideCardProps = {
  imagePosition: CollectionImagePosition;
  mode?: CollectionCardSideBySideMode;
  gap: CollectionGap;
  image: ((className: string) => ReactNode) | null;
  content: ReactNode;
};

const RESPONSIVE_CLASSNAMES: Record<
  CollectionCardSideBySideMode,
  { row: string; media: string; mediaOrderFirst: string; mediaOrderLast: string; content: string }
> = {
  carousel: {
    row: 'h-full md:@xs:flex-row md:@xs:items-center md:@xs:gap-6',
    media: 'md:@xs:w-2/5 md:@xs:mx-0',
    mediaOrderFirst: 'md:@xs:order-1',
    mediaOrderLast: 'md:@xs:order-2',
    content: 'md:@xs:self-stretch md:@xs:flex md:@xs:flex-col md:@xs:justify-center',
  },
  // Alternating cards have padding of their own, so they need more room before
  // splitting into two columns than a plain card does.
  alternating: {
    row: '@md:flex-row @md:items-start @md:gap-6',
    media: '@md:w-2/5 @md:mx-0',
    mediaOrderFirst: '@md:order-1',
    mediaOrderLast: '@md:order-2',
    content: '@md:self-stretch @md:flex @md:flex-col @md:justify-center',
  },
  // In a narrow grid a chosen side is still respected, using a slimmer media
  // column and a smaller gap so both sides remain readable.
  container: {
    row: 'md:@min-[10.5rem]:flex-row md:@min-[10.5rem]:items-start md:@min-[10.5rem]:gap-2 md:@xs:gap-6',
    media: 'md:@min-[10.5rem]:w-1/3 md:@min-[10.5rem]:mx-0 md:@xs:w-2/5',
    mediaOrderFirst: 'md:@min-[10.5rem]:order-1',
    mediaOrderLast: 'md:@min-[10.5rem]:order-2',
    content:
      'md:@min-[10.5rem]:self-stretch md:@min-[10.5rem]:flex md:@min-[10.5rem]:flex-col md:@min-[10.5rem]:justify-center',
  },
};

export function SideBySideCard({
  imagePosition,
  mode = 'container',
  gap,
  image,
  content,
}: SideBySideCardProps) {
  if (!image) return <>{content}</>;
  const imageFirst = imagePosition === 'left';
  const responsiveClassNames = RESPONSIVE_CLASSNAMES[mode];
  const imageNode = image(
    cn(
      'w-full shrink-0',
      responsiveClassNames.media,
      imageFirst ? responsiveClassNames.mediaOrderFirst : responsiveClassNames.mediaOrderLast,
    ),
  );

  if (!imageNode) return <>{content}</>;

  return (
    <div className={cn('flex flex-col', responsiveClassNames.row, getCardContentGapClassName(gap))}>
      {imageNode}
      <div
        className={cn(
          'min-w-0 flex-1',
          responsiveClassNames.content,
          imageFirst ? responsiveClassNames.mediaOrderLast : responsiveClassNames.mediaOrderFirst,
        )}
      >
        {content}
      </div>
    </div>
  );
}
